import moment from "moment";
import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import useClickOutside from "@/hooks/useClickOutSide";
import { Calendar, ChevronLeft, ChevronRight } from "lucide-react";
import { FORMATE } from "@/utils/constant/global.constant";

interface CustomDatePickerProps {
  value?: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  minDate?: string;
  maxDate?: string;
  maxToday?: boolean;
  className?: string;
}

const CustomDatePicker: React.FC<CustomDatePickerProps> = ({ 
  value, 
  onChange, 
  placeholder = "Select date",
  disabled = false,
  minDate,
  maxDate,
  maxToday = false,
  className = ""
}) => {
  const [currentDate, setCurrentDate] = useState(() => {
    if (value) {
      return moment(value, [FORMATE.DATE, "YYYY-MM-DD"]);
    }
    return moment();
  });
  const [showCalendar, setShowCalendar] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const dropdownRef = useClickOutside(() => setShowCalendar(false));
  const inputRef = useRef<HTMLInputElement>(null);
  const [view] = useState<"month" | "year">("month");

  const handleNavigation = (direction: "prev" | "next") => {
    if (view === "month") {
      setCurrentDate(direction === "next" ? moment(currentDate).add(1, "month") : moment(currentDate).subtract(1, "month"));
    } else {
      setCurrentDate(direction === "next" ? moment(currentDate).add(1, "year") : moment(currentDate).subtract(1, "year"));
    }
  };

  const isValidDate = (date: moment.Moment) => {
    const minDateMoment = minDate ? moment(minDate) : null;
    const maxDateMoment = maxDate ? moment(maxDate) : null;
    const today = moment();
    
    let isValid = true;
    
    if (minDateMoment && !date.isSameOrAfter(minDateMoment, "day")) {
      isValid = false;
    }
    
    if (maxDateMoment && !date.isSameOrBefore(maxDateMoment, "day")) {
      isValid = false;
    }
    
    if (maxToday && !date.isSameOrBefore(today, "day")) {
      isValid = false;
    }
    
    return isValid;
  };

  const generateCalendarDays = () => {
    const firstDayOfMonth = moment(currentDate).startOf("month");
    const lastDayOfPrevMonth = moment(currentDate).subtract(1, "month").endOf("month");
    const startDay = firstDayOfMonth.day();

    const days = [];

    // Previous month days
    for (let i = startDay - 1; i >= 0; i--) {
      const prevDate = moment(lastDayOfPrevMonth).subtract(i, "days");

      days.push({
        date: lastDayOfPrevMonth.date() - i,
        isCurrentMonth: false,
        month: "prev",
        isValid: isValidDate(prevDate),
      });
    }

    // Current month days
    const daysInMonth = currentDate.daysInMonth();
    for (let i = 1; i <= daysInMonth; i++) {
      const currentDay = moment(currentDate).date(i);

      days.push({
        date: i,
        isCurrentMonth: true,
        month: "current",
        isValid: isValidDate(currentDay),
      });
    }

    // Next month days
    const remainingDays = 42 - days.length;
    for (let i = 1; i <= remainingDays; i++) {
      const nextDate = moment(currentDate).add(1, "month").date(i);

      days.push({
        date: i,
        isCurrentMonth: false,
        month: "next",
        isValid: isValidDate(nextDate),
      });
    }

    return days;
  };

  const getYearRange = () => {
    const currentYear = moment().year();
    const minYear = minDate ? moment(minDate).year() : currentYear - 100;
    const maxYear = maxDate ? moment(maxDate).year() : (maxToday ? currentYear : currentYear + 10);
    
    return Array.from({ length: maxYear - minYear + 1 }, (_, i) => minYear + i);
  };

  // Function to check available space and determine if calendar should open upward
  const checkSpaceAndPosition = () => {
    if (!inputRef.current) return;
    
    const inputRect = inputRef.current.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;
    const calendarHeight = 350; // Approximate height of the calendar
    const calendarWidth = 256; // Minimum width of the calendar
    const buffer = 20; // Small buffer to ensure calendar doesn't touch viewport edges
    const spaceBelow = viewportHeight - inputRect.bottom - buffer;
    const spaceAbove = inputRect.top - buffer;
    
    // Determine if calendar should open upward
    const shouldOpenUpward = spaceBelow < calendarHeight && spaceAbove > calendarHeight;
    
    // Calculate vertical position
    let top = shouldOpenUpward ? inputRect.top - calendarHeight - 8 : inputRect.bottom + 8;
    
    // Ensure top position doesn't go above viewport
    if (top < buffer) {
      top = buffer;
    }
    
    // Ensure bottom doesn't go below viewport
    if (top + calendarHeight > viewportHeight - buffer) {
      top = viewportHeight - calendarHeight - buffer;
    }
    
    // Calculate horizontal position
    let left = inputRect.left;
    let width = Math.max(inputRect.width, calendarWidth);
    
    // Ensure left position doesn't go outside viewport
    if (left < buffer) {
      left = buffer;
    }
    
    // Ensure right doesn't go outside viewport
    if (left + width > viewportWidth - buffer) {
      left = viewportWidth - width - buffer;
    }
    
    setDropdownPosition({ top, left, width });
  };

  // Check space when calendar opens
  useEffect(() => {
    if (showCalendar) {
      checkSpaceAndPosition();
    }
  }, [showCalendar]);

  // Recalculate position on window resize
  useEffect(() => {
    const handleResize = () => {
      if (showCalendar) {
        checkSpaceAndPosition();
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [showCalendar]);

  return (
    <div className={`relative w-full ${className}`}>
      <div className="relative w-full text-13">
        <div className="relative w-full">
          <input
            ref={inputRef}
            type="text"
            value={value ? moment(value).format(FORMATE.DATE) : ""}
            onClick={() => {
              if (value) {
                setCurrentDate(moment(value, [FORMATE.DATE, "YYYY-MM-DD"]));
              }
              setShowCalendar(true);
            }}
            placeholder={placeholder}
            readOnly
            disabled={disabled}
            className={`
              w-full px-3 py-2 text-text-default rounded-lg outline-none border
              border-fieldBorder
              ${disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer"}
              hover:border-gray-400 focus:border-text-primary-500
            `}
          />
          <span className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
            <Calendar className="text-gray-400 size-4" />
          </span>
        </div>

        {showCalendar && createPortal(
          <div 
            ref={dropdownRef}
            className="fixed border rounded-lg shadow-calender z-[9999] bg-white"
            style={{
              top: dropdownPosition.top,
              left: dropdownPosition.left,
              width: dropdownPosition.width,
              minWidth: '256px'
            }}
          >
            {/* Header */}
            <div className="flex items-center justify-between mb-4 border-b-1 p-2 py-4">
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleNavigation("prev");
                }}
                className="hover:bg-gray-100"
              >
                <ChevronLeft className=" text-icon-default2 size-4" />
              </button>
              <div className=" flex justify-center flex-1 gap-2 flex-wrap">
                <div className=" flex items-center gap-2">
                  <select
                    value={currentDate.year()}
                    onChange={(e) => {
                      setCurrentDate(moment(currentDate).year(parseInt(e.target.value)));
                    }}
                    className="w-fit px-2 py-0.5  text-text-default text-13 rounded-lg outline-none border border-fieldBorder hover:border-gray-400 focus:border-text-primary-500"
                  >
                    {getYearRange().map((year) => (
                      <option key={year} value={year}>
                        {year}
                      </option>
                    ))}
                  </select>
                  <select
                    value={currentDate.month()}
                    onChange={(e) => {
                      setCurrentDate(moment(currentDate).month(parseInt(e.target.value)));
                    }}
                    className="w-fit px-2 py-0.5  text-text-default text-13 rounded-lg outline-none border border-fieldBorder hover:border-gray-400 focus:border-text-primary-500"
                  >
                    {moment.months().map((month, index) => (
                      <option key={month} value={index}>
                        {month}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleNavigation("next");
                }}
                className="hover:bg-gray-100"
              >
                <ChevronRight className="text-icon-default2 size-4" />
              </button>
            </div>
            {/* Calendar Grid */}
            <div className="grid grid-cols-7 gap-1 p-2">
              {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((day) => (
                <div key={day} className="text-center  font-medium py-1 text-gray-600">
                  {day}
                </div>
              ))}

              {generateCalendarDays().map((day, index) => (
                <button
                  key={index}
                  disabled={!day.isValid}
                  onClick={() => {
                    const newDate = moment(currentDate);
                    if (day.month === "prev") {
                      newDate.subtract(1, "month");
                    } else if (day.month === "next") {
                      newDate.add(1, "month");
                    }
                    newDate.date(day.date);
                    onChange(newDate.format("YYYY-MM-DD"));
                    setShowCalendar(false);
                  }}
                  className={`
                    p-2 text-center
                    ${!day.isCurrentMonth ? "text-gray-400" : "text-gray-700"}
                    ${!day.isValid ? "opacity-50 cursor-not-allowed bg-gray-100" : ""}
                    ${
                      moment(value).isSame(moment(currentDate).clone().set("date", day.date), "day") && day.isCurrentMonth
                        ? "bg-purple-100 text-purple-600"
                        : "hover:bg-gray-100"
                    }
                  `}
                >
                  {day.date}
                </button>
              ))}
            </div>
          </div>,
          document.body
        )}
      </div>
    </div>
  );
};

export default CustomDatePicker;
